import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export const StripePayoutsCard = ({ connected, onPushed }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/xero/stripe-payouts").then((r) => setD(r.data)).catch(() => setD({ waiting: [], pushed: [], stripe_error: "Couldn't load payouts" }));
  }, []);
  useEffect(() => { load(); }, [load]);
  const push = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/xero/stripe-payouts/push");
      const made = res.pushed.filter((p) => p.invoice_number).length;
      const covered = res.pushed.length - made;
      if (made) toast.success(`Created ${made} Xero payout entr${made === 1 ? "y" : "ies"}`);
      if (covered) toast.info(`${covered} payout(s) were already in Xero via the old weekly batch — marked as done`);
      if (!res.pushed.length && !res.errors.length) toast.info("No new Stripe payouts to send");
      if (res.errors.length) toast.error(`${res.errors.length} payout(s) failed: ${res.errors[0].error}`);
      load(); onPushed && onPushed();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="stripe-payouts-card">
      <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle className="text-lg">Stripe payouts → Xero (reconciliation)</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">One "Stripe Sales" invoice per Stripe payout — a line per sale, less Stripe fees — so its total exactly matches the deposit in your bank. Match them in Xero's bank reconciliation.</p>
        </div>
        <Button onClick={push} disabled={busy || !connected || !d || d.waiting.length === 0} data-testid="stripe-payouts-push-button" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Send new payouts to Xero
        </Button>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {!d ? <p className="text-muted-foreground">Loading…</p> : (
          <>
            {d.stripe_error && <p className="text-kilted-gold" data-testid="stripe-payouts-error">{d.stripe_error}</p>}
            {d.waiting.length === 0 ? <p className="text-muted-foreground" data-testid="stripe-payouts-none-waiting">No new payouts waiting.</p> : (
              <div data-testid="stripe-payouts-waiting">
                <p className="font-semibold">Waiting to send</p>
                {d.waiting.map((p) => <p key={p.payout_id}>{p.arrival_date} · {money(p.amount)}</p>)}
              </div>
            )}
            {d.pushed.length > 0 && (
              <div data-testid="stripe-payouts-pushed">
                <p className="font-semibold">Recently sent</p>
                {d.pushed.map((p) => (
                  <p key={p.payout_id} data-testid={`stripe-payout-sent-${p.payout_id}`}>
                    {p.invoice_number
                      ? `${p.arrival_date} · deposit ${money(p.payout_amount)} = sales ${money(p.gross)} − fees ${money(p.fees)} · ${p.invoice_number}`
                      : `${p.arrival_date} · deposit ${money(p.payout_amount)} · ${p.note}`}
                  </p>
                ))}
              </div>
            )}
          </>
        )}
        <p className="text-xs text-muted-foreground">Checks automatically every Monday and Tuesday at 6pm Sydney time for new payouts.</p>
      </CardContent>
    </Card>
  );
};
